/**
 * `ReviewByKind` — центр разбора хозяйства: РАЗНЫЙ шаблон на каждый тип.
 *
 * API:
 *   const review = useFarmReview(kind, farmId, scope);      // данные и карта
 *   <ReviewByKind kind={kind} review={review} scope={scope}
 *                 farmId={id} situation={строка очереди?}
 *                 districtFarms={picture.summary.by_farm?} />
 *   - `kind` — тип из `problems/model.ts` (`deadlines | water | conflicts |
 *     quarantine | overlap`), обычно `reviewKindFor(tab, situation)`;
 *   - `districtFarms` — разрез района по хозяйствам для строки «доля против
 *     медианы» (Сроки); нет — строка не рисуется;
 *   - кнопка «Сверить» (Расхождения) видна только роли `manager`.
 * Шаблоны:
 *   Сроки — 3 работы с наибольшей просрочкой, окна ≤5 дн., доля vs медиана;
 *   Вода — поливы хозяйства «запрошено → подано, разрыв; без отчёта на N
 *          контурах» (строка выбирает полив для карты);
 *   Расхождения — спорные обязательства: работа, окно, контуры, отметка;
 *   Карантин — контуры красн./жёлт., вредитель, срок предписания, просрочка;
 *   Совпадение — Сроки по поливам + Вода + «на одних и тех же K контурах».
 */
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, CalendarClock, ChevronDown, ChevronUp, Droplets, Scale, ShieldAlert } from 'lucide-react';
import type { Schemas } from '../../../api/client';
import { usePersona } from '../../auth/RoleContext';
import { Badge } from '../../ui/Badge';

import { LoadError } from '../../ui/LoadError';
import { InfoPopover } from '../../../components/InfoPopover';
import { Icon } from '../../ui/Icon';
import { SkeletonList, SkeletonRegion, SkeletonText } from '../../ui/Skeleton';
import { countOf, dateDay, haUnit, m3Compact, num, pct, WORDS } from '../../lib/format';
import { defineMessages } from '../../../i18n/define';
import { useT } from '../../../i18n/hooks';
import { tr } from '../../../i18n/runtime';
import type { Situation } from '../situations';
import { daysFrom, outcomeLabel, shareVsMedian, type IrrigationRow, type OverdueWork, type ProblemKind } from './model';
import { workCycle, type FarmReview, type ProblemScope } from './useFarmReview';
import './problems.css';

type BreakdownRow = Schemas['SeasonBreakdownRowOut'];

export interface ReviewByKindProps {
  kind: ProblemKind;
  review: FarmReview;
  scope: ProblemScope;
  farmId: number;
  situation?: Situation | null;
  districtFarms?: readonly BreakdownRow[] | null;
}

const m = defineMessages({
  reviewWhat: { id: 'board.review.what', defaultMessage: 'разбор хозяйства', description: 'Что не загрузилось / грузится (винительный падеж): подставляется в «Не удалось загрузить …» и в подпись скелетона для диктора' },
  days: { id: 'board.review.days', defaultMessage: '{count, plural, one {# день} few {# дня} many {# дней} other {# дня}}', description: 'Число дней словом: «1 день», «3 дня», «5 дней»' },
  closedNone: { id: 'board.review.closed.none', defaultMessage: 'окно не названо', description: 'Строка работы в разборе «Сроки»: у работы не названо окно (строчными, после названия работы)' },
  closedAgo: { id: 'board.review.closed.ago', defaultMessage: 'окно закрыто {days} назад', description: 'Строка работы: окно закрылось раньше; {days} — число дней словом («5 дней»)' },
  closedToday: { id: 'board.review.closed.today', defaultMessage: 'окно закрывается сегодня', description: 'Строка работы: последний день окна' },
  closedLeft: { id: 'board.review.closed.left', defaultMessage: 'до конца окна {days}', description: 'Строка работы: окно ещё идёт; {days} — осталось дней словом («3 дня»)' },
  secMost: { id: 'board.review.deadlines.most', defaultMessage: 'Больше всего после срока', description: 'Заголовок раздела в разборе «Сроки»: работы с наибольшим числом работ на полях после срока' },
  worksWithArea: { id: 'board.review.worksWithArea', defaultMessage: '{works} · {area}', description: 'Число работ на полях и площадь земли в строке работы; {works} — «12 работ на полях», {area} — «46,8 га»' },
  overdueEmpty: { id: 'board.review.deadlines.empty', defaultMessage: 'Работ на полях после срока без отчёта нет на эту дату.', description: 'Пустое состояние раздела «Больше всего после срока»' },
  secSoon: { id: 'board.review.deadlines.soon', defaultMessage: 'Окна закрываются в ближайшие 5 дней', description: 'Заголовок раздела в разборе «Сроки»: окна, которые вот-вот закроются' },
  soonNum: { id: 'board.review.deadlines.soonNum', defaultMessage: '{count} без отчёта', description: 'Правая колонка строки работы с закрывающимся окном; {count} — число работ на полях без отчёта' },
  soonMeta: { id: 'board.review.deadlines.soonMeta', defaultMessage: 'до {date} · ещё не срок', description: 'Подпись строки работы с закрывающимся окном; {date} — день и месяц конца окна' },
  soonEmpty: { id: 'board.review.deadlines.soonEmpty', defaultMessage: 'Окон, которые закрываются в ближайшие пять дней и ждут отчёта, нет.', description: 'Пустое состояние раздела про закрывающиеся окна' },
  medianNotComputed: { id: 'board.review.deadlines.medianNone', defaultMessage: 'не посчитана', description: 'Вместо медианы, когда её нельзя посчитать (подставляется в строку про долю против медианы)' },
  vsMedian: {
    id: 'board.review.deadlines.vsMedian',
    defaultMessage: 'После срока — <b>{share}</b> работ на полях хозяйства; медиана {peers, plural, one {# хозяйства} few {# хозяйств} many {# хозяйств} other {# хозяйств}} района — {median}.',
    description: 'Сравнение хозяйства с районом в разборе «Сроки» (менеджер); {share}/{median} — проценты («12 %») или «не посчитана», {peers} — число хозяйств района в сравнении; <b> выделяет долю хозяйства',
  },
  aheadLine: { id: 'board.review.water.ahead', defaultMessage: 'окно с {date} — ещё не срок', description: 'Строка полива в разборе «Вода»: окно полива ещё не открылось; {date} — день и месяц начала' },
  noVolume: { id: 'board.review.water.noVolume', defaultMessage: 'объёма по поливу в записях нет', description: 'Строка полива: в журнале нет заявок и подачи по этому поливу' },
  unmeasuredVolume: { id: 'board.review.water.unmeasured', defaultMessage: 'объём не измерен', description: 'Строка полива: объём заявки или подачи не измерен (пробел учёта, не ноль)' },
  volume: { id: 'board.review.water.volume', defaultMessage: 'запрошено {requested} → подано {delivered}', description: 'Строка полива: заявка и подача; {requested}/{delivered} — объёмы с единицей («1,2 млн м³»)' },
  volumeGap: { id: 'board.review.water.volumeGap', defaultMessage: 'запрошено {requested} → подано {delivered}, недоподача {gap}', description: 'Строка полива с недоподачей; {requested}/{delivered}/{gap} — объёмы с единицей' },
  waterExtras: {
    id: 'board.review.water.extras',
    defaultMessage: '{volume}{hasSilent, select, yes {; отчёта нет на {silent}} other {}}{hasGap, select, yes {; подача не подтверждена на {gapContours}} other {}}',
    description: 'Подпись строки полива в разборе «Вода»; {volume} — фраза про объёмы, {silent}/{gapContours} — число контуров словом («5 контуров») в предложных фразах; части через «;» появляются, только если есть контуры',
  },
  irrigationLabel: { id: 'board.review.water.listLabel', defaultMessage: 'поливы', description: 'Что грузится (винительный падеж, множественное число): подпись скелетона списка поливов для диктора' },
  irrigationEmpty: { id: 'board.review.water.empty', defaultMessage: 'Поливов у хозяйства в этом агроцикле нет.', description: 'Пустое состояние списка поливов' },
  deficit: { id: 'board.review.water.deficit', defaultMessage: '−{value}', description: 'Недоподача в правой колонке строки полива: знак минус и объём («−1,2 млн м³»)' },
  noVolumeShort: { id: 'board.review.water.noVolumeShort', defaultMessage: 'нет объёма', description: 'Правая колонка строки полива: объёмов в записях нет; коротко' },
  noGap: { id: 'board.review.water.noGap', defaultMessage: 'без разрыва', description: 'Правая колонка строки полива: заявка и подача сошлись; коротко' },
  secIrrigation: { id: 'board.review.water.section', defaultMessage: 'Поливы хозяйства: заявка и подача', description: 'Заголовок раздела в разборе «Вода»' },
  waterSummary: {
    id: 'board.review.water.summary',
    defaultMessage: 'Недоподача по начавшимся поливам — <b>{gap}</b>{unmeasured, plural, =0 {} one {; по # поливу объём не измерен — это пробел учёта, а не ноль} few {; по # поливам объём не измерен — это пробел учёта, а не ноль} many {; по # поливам объём не измерен — это пробел учёта, а не ноль} other {; по # поливам объём не измерен — это пробел учёта, а не ноль}}. Нажмите полив, чтобы увидеть его на карте.',
    description: 'Итоговая строка под списком поливов (менеджер); {gap} — суммарная недоподача с единицей, {unmeasured} — число поливов без измеренного объёма; <b> выделяет недоподачу',
  },
  evWindow: { id: 'board.review.water.evWindow', defaultMessage: 'окно {from}–{to}', description: 'Фрагмент строки полива в разборе «Вода»: окно полива; {from}/{to} — день и месяц' },
  evWatered: { id: 'board.review.water.evWatered', defaultMessage: 'полито {closed} из {total} по плану', description: 'Фрагмент строки полива: сколько поливов (контур × полив) закрыто событием файла ирригации из плана хозяйства; числа' },
  evVolume: { id: 'board.review.water.evVolume', defaultMessage: '{volume} по файлу', description: 'Фрагмент строки полива: объём воды по событиям файла ирригации; {volume} — «1,2 млн м³»' },
  evVolumeNone: { id: 'board.review.water.evVolumeNone', defaultMessage: 'объём в файле не указан', description: 'Фрагмент строки полива: в событиях файла объём не указан (не ноль)' },
  evDry: { id: 'board.review.water.evDry', defaultMessage: 'без подачи: {list}{more, plural, =0 {} other { и ещё #}}', description: 'Фрагмент строки полива: контуры, где окно закрылось, а события файла нет; {list} — «№2831, №2814», {more} — сколько не перечислено' },
  evDryNum: { id: 'board.review.water.evDryNum', defaultMessage: '{count} без подачи', description: 'Правая колонка строки полива: контуров без подачи (окно закрылось, события нет); коротко' },
  evAll: { id: 'board.review.water.evAll', defaultMessage: 'полито', description: 'Правая колонка строки полива: все поливы с закрытым окном закрыты событием файла; коротко, строчными' },
  evWaiting: { id: 'board.review.water.evWaiting', defaultMessage: 'окно идёт', description: 'Правая колонка строки полива: окно ещё открыто, без подачи пока никого нет; коротко, строчными' },
  evSection: { id: 'board.review.water.evSection', defaultMessage: 'Поливы хозяйства по файлу ирригации', description: 'Заголовок раздела в разборе «Вода»: поливы плана хозяйства и события файла ирригации' },
  evInfo: { id: 'board.review.water.evInfo', defaultMessage: 'Факт полива — событие файла ирригации: оно само закрывает полив плана на контуре. «Без подачи» — окно полива закрылось, а события в файле нет; это повод уточнить у водной службы, а не вывод «не полито».', description: 'Подсказка «i» у заголовка раздела поливов в разборе «Вода»: откуда факт полива и что значит «без подачи»' },
  evSummary: {
    id: 'board.review.water.evSummary',
    defaultMessage: 'Без подачи — <b>{dry, plural, one {# полив} few {# полива} many {# поливов} other {# полива}}</b> (контур × полив с закрытым окном) в {works, plural, one {# поливе} few {# поливах} many {# поливах} other {# поливах}} плана. Нажмите полив, чтобы увидеть его контуры на карте.',
    description: 'Итог под списком проблемных поливов в разборе «Вода»; {dry} — поливов (контур × полив) без события файла ирригации, {works} — поливов плана, где они есть; <b> выделяет число',
  },
  irrigationNoProblem: { id: 'board.review.water.noProblem', defaultMessage: 'Поливов без подачи на эту дату нет.', description: 'Разбор «Вода»: среди начавшихся поливов нет ни одного с проблемой' },
  okIrrigation: { id: 'board.review.okIrrigation', defaultMessage: 'Ещё {count, plural, one {# полив} few {# полива} many {# поливов} other {# полива}} без проблем', description: 'Строка-сводка под списком проблемных поливов: сколько поливов без проблем скрыто; нажатие раскрывает' },
  okCorrected: { id: 'board.review.okCorrected', defaultMessage: 'Ещё {count, plural, one {# отчёт} few {# отчёта} many {# отчётов} other {# отчёта}} с исправленным статусом', description: 'Строка-сводка под списком расхождений: сколько уже исправленных отчётов скрыто; нажатие раскрывает' },
  okHide: { id: 'board.review.okHide', defaultMessage: 'Свернуть', description: 'Кнопка: свернуть раскрытые строки без проблем' },
  secConflicts: { id: 'board.review.conflicts.section', defaultMessage: 'Спорные обязательства', description: 'Заголовок раздела в разборе «Расхождения» (менеджер): обязательства хозяйства, по которым отчёт оспорен' },
  frameFallback: { id: 'board.review.conflicts.frame', defaultMessage: 'Отчёт остаётся фактом, пока расхождение не уточнено; отметка проверяющего — повод уточнить, а не отмена отчёта.', description: 'Методическая подсказка «i» у заголовка раздела «Спорные обязательства», если сервер своей не прислал' },
  queueLabel: { id: 'board.review.conflicts.queueLabel', defaultMessage: 'очередь проверки', description: 'Что грузится (винительный падеж): подпись скелетона для диктора' },
  conflictsEmpty: { id: 'board.review.conflicts.empty', defaultMessage: 'Опровергнутых отчётов и просьб уточнить у хозяйства в этом агроцикле нет.', description: 'Пустое состояние раздела «Спорные обязательства»' },
  contourNo: { id: 'board.review.contourNo', defaultMessage: 'контур №{no}', description: 'Правая колонка строки расхождения: один контур; {no} — номер контура; строчными' },
  corrected: { id: 'board.review.conflicts.corrected', defaultMessage: 'статус исправлен', description: 'Бейдж в строке расхождения: статус отчёта исправлен после проверки; строчными, коротко' },
  reportedOn: { id: 'board.review.conflicts.reportedOn', defaultMessage: 'отчёт {date}', description: 'Фрагмент подписи строки расхождения; {date} — день и месяц отчёта' },
  reportedNone: { id: 'board.review.conflicts.reportedNone', defaultMessage: 'дата отчёта не названа', description: 'Фрагмент подписи строки расхождения: у отчёта нет даты' },
  checkedOn: { id: 'board.review.conflicts.checkedOn', defaultMessage: 'отметка {date}', description: 'Фрагмент подписи строки расхождения: когда проверяющий поставил отметку' },
  correctedOn: { id: 'board.review.conflicts.correctedOn', defaultMessage: 'исправлено {date}', description: 'Фрагмент подписи строки расхождения: когда статус исправлен' },
  correctedOnBy: { id: 'board.review.conflicts.correctedOnBy', defaultMessage: 'исправлено {date}, {by}', description: 'Фрагмент подписи строки расхождения: когда и кем статус исправлен; {by} — имя или роль' },
  noteQuote: { id: 'board.review.conflicts.note', defaultMessage: '«{note}»', description: 'Примечание проверяющего в кавычках; {note} — его текст' },
  windowTo: { id: 'board.review.conflicts.windowTo', defaultMessage: 'окно до {date}', description: 'Фрагмент подписи строки расхождения: конец окна работы' },
  moreInQueue: { id: 'board.review.conflicts.more', defaultMessage: 'Ещё {count} в очереди проверки.', description: 'Под списком расхождений: сколько строк не поместилось; {count} — число' },
  verify: { id: 'board.review.conflicts.verify', defaultMessage: 'Сверить', description: 'Кнопка (менеджер): открыть очередь проверки по этому хозяйству; рядом стрелка' },
  secQuarantine: { id: 'board.review.quarantine.section', defaultMessage: 'Контуры с карантинным сигналом', description: 'Заголовок раздела в разборе «Карантин»' },
  quarantineLabel: { id: 'board.review.quarantine.label', defaultMessage: 'статусы карантина', description: 'Что грузится (винительный падеж, множественное число): подпись скелетона для диктора' },
  quarantineEmpty: { id: 'board.review.quarantine.empty', defaultMessage: 'Красных и жёлтых контуров и предписаний после срока на земле хозяйства нет на эту дату.', description: 'Пустое состояние раздела «Контуры с карантинным сигналом»' },
  contourTitle: { id: 'board.review.quarantine.contour', defaultMessage: 'Контур №{no}', description: 'Название строки карантина: контур; {no} — номер контура' },
  statusRed: { id: 'board.review.quarantine.red', defaultMessage: 'красный с {date}', description: 'Статус карантина контура (цветной бейдж): красный, с даты; строчными' },
  statusYellow: { id: 'board.review.quarantine.yellow', defaultMessage: 'жёлтый с {date}', description: 'Статус карантина контура (цветной бейдж): жёлтый, с даты; строчными' },
  statusGreen: { id: 'board.review.quarantine.green', defaultMessage: 'зелёный с {date}', description: 'Статус карантина контура (цветной бейдж): зелёный, с даты; строчными' },
  pestNone: { id: 'board.review.quarantine.pestNone', defaultMessage: 'вредитель не назван', description: 'Фрагмент подписи строки карантина: вредный организм не назван' },
  orderUntil: { id: 'board.review.quarantine.orderUntil', defaultMessage: 'предписание до {date}', description: 'Фрагмент подписи строки карантина: срок предписания' },
  orderNone: { id: 'board.review.quarantine.orderNone', defaultMessage: 'предписания нет', description: 'Фрагмент подписи строки карантина: предписания нет' },
  orderOverdue: { id: 'board.review.quarantine.orderOverdue', defaultMessage: 'после срока {days}', description: 'Фрагмент подписи строки карантина: предписание просрочено; {days} — дней словом' },
  secOverlap: { id: 'board.review.overlap.section', defaultMessage: 'Поливы без отчёта после срока', description: 'Заголовок раздела в разборе «Сроки и вода»' },
  overlapMetaClosed: { id: 'board.review.overlap.metaClosed', defaultMessage: 'окно до {date} · закрыто {days} назад', description: 'Подпись строки полива без отчёта; {date} — конец окна, {days} — дней словом' },
  overlapMetaOpen: { id: 'board.review.overlap.metaOpen', defaultMessage: 'окно до {date} · ещё не срок', description: 'Подпись строки полива без отчёта: окно ещё открыто; {date} — конец окна' },
  overlapEmpty: { id: 'board.review.overlap.empty', defaultMessage: 'Поливов без отчёта на контурах нет.', description: 'Пустое состояние раздела «Поливы без отчёта после срока»' },
  secSameIrrigation: { id: 'board.review.overlap.sameIrrigation', defaultMessage: 'Заявка и подача по тем же поливам', description: 'Заголовок раздела в разборе «Сроки и вода»' },
  sameContours: {
    id: 'board.review.overlap.sameContours',
    defaultMessage: 'На одних и тех же <b>{count, plural, one {# контуре} few {# контурах} many {# контурах} other {# контурах}}</b> выбранного полива нет ни отчёта, ни полной подачи.',
    description: 'Итог в разборе «Сроки и вода»: на скольких контурах выбранного полива нет ни отчёта, ни подачи; <b> выделяет число',
  },
  sameNamed: {
    id: 'board.review.overlap.sameNamed',
    defaultMessage: 'Совпадение названо по <b>{count, plural, one {# работе на поле} few {# работам на полях} many {# работам на полях} other {# работам на полях}}</b> полива: отчёта нет, водная строка есть.',
    description: 'Итог в разборе «Сроки и вода», когда контуры не перечислены; <b> выделяет число',
  },
  sameNone: { id: 'board.review.overlap.sameNone', defaultMessage: 'Совпадения отчёта и подачи на одних контурах в выбранном поливе нет.', description: 'Итог в разборе «Сроки и вода»: совпадения нет' },
});

const joinParts = (parts: (string | null | undefined | false)[]) => parts.filter(Boolean).join(' · ');
const strong = (c: ReactNode) => <strong>{c}</strong>;

export function ReviewByKind(props: ReviewByKindProps) {
  const { kind, review } = props;
  const t = useT();
  if (review.error) return <LoadError what={t(m.reviewWhat)} error={review.error} onRetry={review.reload} compact />;
  if (review.loading && !review.picture) return <SkeletonRegion label={t(m.reviewWhat)} minHeight={160}><SkeletonText lines={5} /></SkeletonRegion>;
  switch (kind) {
    case 'deadlines':
      return <DeadlinesReview {...props} />;
    case 'water':
      return <WaterReview {...props} />;
    case 'conflicts':
      return <ConflictsReview {...props} />;
    case 'quarantine':
      return <QuarantineReview {...props} />;
    case 'overlap':
      // Вода по событиям: «срок прошёл и подачи нет» — тот же полив без события.
      return review.irrigation?.some(r => r.fromEvents) ? <WaterReview {...props} /> : <OverlapReview {...props} />;
  }
}

function Section({ icon, title, info, children }: { icon: typeof Droplets; title: string; info?: ReactNode; children: ReactNode }) {
  return (
    <div className="review__section">
      <h4 className="review__h"><Icon icon={icon} size={16} />{title}{info ? <InfoPopover icon="info" label={title}>{info}</InfoPopover> : null}</h4>
      {children}
    </div>
  );
}

const workHref = (scope: ProblemScope, w: { campaignId: number; cycle?: string | null }) => `/cycles/${workCycle(scope, w.cycle)}/works/${w.campaignId}${scope.linkSearch()}`;

/** Имя работы ссылкой, если кампания и её цикл известны (из картины хозяйства). */
function WorkName({ scope, w }: { scope: ProblemScope; w: { campaignId: number; name: string; cycle?: string | null } }) {
  return w.campaignId && workCycle(scope, w.cycle) ? <Link to={workHref(scope, w)} className="review__name">{w.name}</Link> : <span className="review__name">{w.name}</span>;
}

function closedPhrase(w: OverdueWork): string {
  if (w.closedDaysAgo === null) return tr(m.closedNone);
  if (w.closedDaysAgo > 0) return tr(m.closedAgo, { days: tr(m.days, { count: w.closedDaysAgo }) });
  return w.closedDaysAgo === 0 ? tr(m.closedToday) : tr(m.closedLeft, { days: tr(m.days, { count: -w.closedDaysAgo }) });
}

/* ------------------------------------------------------------ Сроки */

function DeadlinesReview({ review, scope, districtFarms }: ReviewByKindProps) {
  const t = useT();
  const pic = review.picture;
  const overdueTotal = pic?.overdue.value ?? null;
  const cmp = pic && districtFarms?.length ? shareVsMedian({ overdue: overdueTotal, total: pic.obligations.total }, districtFarms) : null;
  return (
    <div className="review">
      <Section icon={CalendarClock} title={t(m.secMost)}>
        {review.overdue.length ? (
          <ul className="review__list">
            {review.overdue.map(w => (
              <li key={w.campaignId} className="review__row">
                <WorkName scope={scope} w={w} />
                <span className="review__num">{w.areaHa !== null ? t(m.worksWithArea, { works: countOf(w.overdue, WORDS.obligation), area: haUnit(w.areaHa) }) : countOf(w.overdue, WORDS.obligation)}</span>
                <span className="review__meta">{closedPhrase(w)}</span>
              </li>
            ))}
          </ul>
        ) : <p className="review__empty">{t(m.overdueEmpty)}</p>}
      </Section>
      <Section icon={CalendarClock} title={t(m.secSoon)}>
        {review.soon.length ? (
          <ul className="review__list">
            {review.soon.map(w => (
              <li key={w.campaignId} className="review__row">
                <WorkName scope={scope} w={w} />
                <span className="review__num">{t(m.soonNum, { count: num(w.noReport) })}</span>
                <span className="review__meta">{t(m.soonMeta, { date: dateDay(w.windowTo) })}</span>
              </li>
            ))}
          </ul>
        ) : <p className="review__empty">{t(m.soonEmpty)}</p>}
      </Section>
      {cmp && cmp.share !== null && (cmp.share >= 0.5 || (cmp.median ?? 0) >= 0.5) ? (
        <p className="review__line">
          {t(m.vsMedian, { share: pct(cmp.share), peers: cmp.peers, median: cmp.median === null ? t(m.medianNotComputed) : pct(cmp.median), b: strong })}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ Вода */

function waterLine(r: IrrigationRow): string {
  if (r.ahead) return tr(m.aheadLine, { date: dateDay(r.windowFrom) });
  const volume = r.noRequests
    ? tr(m.noVolume)
    : r.requested === null || r.delivered === null
    ? tr(m.unmeasuredVolume)
    : r.gap
    ? tr(m.volumeGap, { requested: m3Compact(r.requested), delivered: m3Compact(r.delivered), gap: m3Compact(r.gap) })
    : tr(m.volume, { requested: m3Compact(r.requested), delivered: m3Compact(r.delivered) });
  const gapN = r.gapContoursTotal ?? 0;
  return tr(m.waterExtras, {
    volume,
    hasSilent: r.silentContours.length ? 'yes' : 'no',
    silent: countOf(r.silentContours.length, WORDS.contour),
    hasGap: gapN ? 'yes' : 'no',
    gapContours: countOf(gapN, WORDS.contour),
  });
}

/** Строка полива по событиям файла ирригации (agroai-0e8): что, где, сколько. */
function eventLine(r: IrrigationRow): string {
  if (r.ahead) return tr(m.aheadLine, { date: dateDay(r.windowFrom) });
  return joinParts([
    r.windowFrom && r.windowTo ? tr(m.evWindow, { from: dateDay(r.windowFrom), to: dateDay(r.windowTo) }) : null,
    typeof r.obligations === 'number' ? tr(m.evWatered, { closed: num(r.closed ?? 0), total: num(r.obligations) }) : null,
    typeof r.wateredHa === 'number' && r.wateredHa > 0 ? haUnit(r.wateredHa) : null,
    r.events ? (r.delivered === null ? tr(m.evVolumeNone) : tr(m.evVolume, { volume: m3Compact(r.delivered) })) : null,
  ]);
}

/** Где нет подачи: номера контуров (первые — от сервера) и сколько ещё. */
function eventDry(r: IrrigationRow): string | null {
  const dry = r.gapContoursTotal ?? 0;
  if (r.ahead || !dry) return null;
  const nos = (r.gapContourNos ?? []).map(no => `№${no}`);
  return tr(m.evDry, { list: nos.join(', '), more: Math.max(0, dry - nos.length) });
}

function eventNum(r: IrrigationRow, asOf: string): string {
  const dry = r.gapContoursTotal ?? 0;
  if (dry) return tr(m.evDryNum, { count: num(dry) });
  return r.windowTo && r.windowTo >= asOf ? tr(m.evWaiting) : tr(m.evAll);
}

/** Полив с проблемой: по событиям — есть контуры без подачи; по старой цепочке — недоподача. */
const irrigationHasProblem = (r: IrrigationRow) => (r.fromEvents ? (r.gapContoursTotal ?? 0) > 0 : (r.gap ?? 0) > 0 || r.silentContours.length > 0);

function IrrigationList({ review, asOf }: { review: FarmReview; asOf: string }) {
  const t = useT();
  const [showOk, setShowOk] = useState(false);
  const rows = review.irrigation ?? [];
  if (review.irrigation === null) return <SkeletonList label={t(m.irrigationLabel)} items={3} />;
  if (!rows.length) return <p className="review__empty">{t(m.irrigationEmpty)}</p>;
  // «Где нужно разобраться» — только проблемные поливы, тяжёлые сверху;
  // благополучные — одной строкой-сводкой, раскрываются по нажатию.
  const started = rows.filter(r => !r.ahead);
  const bad = started.filter(irrigationHasProblem).sort((a, b) => (b.gapContoursTotal ?? 0) - (a.gapContoursTotal ?? 0) || (b.gap ?? 0) - (a.gap ?? 0));
  const ok = started.filter(r => !irrigationHasProblem(r));
  return (
    <>
    {bad.length ? null : <p className="review__empty">{t(m.irrigationNoProblem)}</p>}
    <ul className="review__list">
      {[...bad, ...(showOk ? ok : [])].map(r => (
        <li key={r.workDefId}>
          <button
            type="button"
            className="review__row review__row--button"
            aria-pressed={review.irrigationId === r.workDefId}
            onClick={() => review.setIrrigationId(r.workDefId)}
          >
            <span className="review__name">{r.name}</span>
            {r.fromEvents ? (
              <span className={`review__num ${r.gapContoursTotal ? 'review__num--warn' : ''}`}>{eventNum(r, asOf)}</span>
            ) : (
              <span className={`review__num ${r.gap ? 'review__num--warn' : ''}`}>{r.gap ? t(m.deficit, { value: m3Compact(r.gap) }) : r.noRequests ? t(m.noVolumeShort) : r.requested === null ? '—' : t(m.noGap)}</span>
            )}
            <span className="review__meta">{r.fromEvents ? eventLine(r) : waterLine(r)}</span>
            {r.fromEvents && eventDry(r) ? <span className="review__meta review__meta--warn">{eventDry(r)}</span> : null}
          </button>
        </li>
      ))}
    </ul>
    {ok.length ? <MoreOk count={ok.length} shown={showOk} onToggle={() => setShowOk(v => !v)} label={m.okIrrigation} /> : null}
    </>
  );
}

/** Строка-сводка «ещё N … без проблем» — раскрыть / свернуть. */
function MoreOk({ count, shown, onToggle, label }: { count: number; shown: boolean; onToggle: () => void; label: typeof m.okIrrigation | typeof m.okCorrected }) {
  const t = useT();
  return (
    <button type="button" className="review__more" aria-expanded={shown} onClick={onToggle}>
      {shown ? t(m.okHide) : t(label, { count })} {shown ? <ChevronUp size={14} aria-hidden /> : <ChevronDown size={14} aria-hidden />}
    </button>
  );
}

function WaterReview({ review, scope }: ReviewByKindProps) {
  const t = useT();
  const rows = (review.irrigation ?? []).filter(r => !r.ahead);
  if (rows.some(r => r.fromEvents)) {
    // Вода — события файла ирригации (agroai-0e8): заявок в пилоте нет, поэтому
    // ни «запрошено», ни «недоподача» здесь не считаются и не называются.
    const badWorks = rows.filter(r => (r.gapContoursTotal ?? 0) > 0).length;

    const dry = rows.reduce((s, r) => s + (r.gapContoursTotal ?? 0), 0);
    return (
      <div className="review">
        <Section icon={Droplets} title={t(m.evSection)} info={t(m.evInfo)}>
          <IrrigationList review={review} asOf={scope.asOf} />
        </Section>
        <p className="review__line">{dry ? t(m.evSummary, { works: badWorks, dry, b: strong }) : null}</p>
      </div>
    );
  }
  const gap = rows.reduce((s, r) => s + (r.gap ?? 0), 0);
  const unmeasured = rows.filter(r => !r.noRequests && (r.requested === null || r.delivered === null)).length;
  return (
    <div className="review">
      <Section icon={Droplets} title={t(m.secIrrigation)}>
        <IrrigationList review={review} asOf={scope.asOf} />
      </Section>
      {rows.length ? (
        <p className="review__line">
          {t(m.waterSummary, { gap: m3Compact(gap), unmeasured, b: strong })}
        </p>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ Расхождения */

function ConflictsReview({ review, farmId, scope }: ReviewByKindProps) {
  const t = useT();
  const { persona } = usePersona();
  const [showCorrected, setShowCorrected] = useState(false);
  const all = review.conflicts;
  // Открытые расхождения — проблема; исправленные — история, свёрнута в сводку.
  const corrected = all ? all.filter(r => r.correctedOn) : [];
  const rows = all ? [...all.filter(r => !r.correctedOn), ...(showCorrected ? corrected : [])] : null;
  const frame = review.breakdown?.conflicts?.frame_note ?? null;
  return (
    <div className="review">
      {/* Правило учёта («исправленный отчёт — статус исправлен, а не проверено»)
          — методика, а не факт разбора: в «i» у заголовка (07.10.2026). */}
      <Section icon={Scale} title={t(m.secConflicts)} info={(frame ?? t(m.frameFallback)).replace(/^Учётная рамка:\s*/, '')}>
        {rows === null ? <SkeletonList label={t(m.queueLabel)} items={3} />
          : rows.length === 0 && !corrected.length ? <p className="review__empty">{t(m.conflictsEmpty)}</p>
          : rows.length === 0 ? null
          : (
            <ul className="review__list">
              {rows.slice(0, 8).map(r => (
                <li key={`${r.farmObligationId}:${r.correctedOn ?? ''}`} className="review__row">
                  <span className="review__name">{r.work}</span>
                  <span className="review__num">{r.contourNo ? t(m.contourNo, { no: r.contourNo }) : countOf(r.contours, WORDS.contour)}</span>
                  <span className="review__meta">
                    <Badge tone={r.correctedOn ? 'neutral' : r.outcome === 'conflict' ? 'outline' : 'neutral'}>{r.correctedOn ? t(m.corrected) : outcomeLabel(r.outcome)}</Badge>
                    {' '}{joinParts(r.contourNo !== undefined
                      ? [
                          r.reportedOn ? t(m.reportedOn, { date: dateDay(r.reportedOn) }) : t(m.reportedNone),
                          r.checkedOn && t(m.checkedOn, { date: dateDay(r.checkedOn) }),
                          r.checkedBy,
                          r.correctedOn && (r.correctedBy ? t(m.correctedOnBy, { date: dateDay(r.correctedOn), by: r.correctedBy }) : t(m.correctedOn, { date: dateDay(r.correctedOn) })),
                          r.note && t(m.noteQuote, { note: r.note }),
                        ]
                      : [
                          t(m.windowTo, { date: dateDay(r.windowTo) }),
                          r.checkedOn && t(m.checkedOn, { date: dateDay(r.checkedOn) }),
                          r.checkedBy,
                        ])}
                  </span>
                </li>
              ))}
            </ul>
          )}
        {rows && rows.length > 8 ? <p className="review__meta">{t(m.moreInQueue, { count: num(rows.length - 8) })}</p> : null}
        {corrected.length ? <MoreOk count={corrected.length} shown={showCorrected} onToggle={() => setShowCorrected(v => !v)} label={m.okCorrected} /> : null}
      </Section>
      {persona?.role === 'manager' ? (
        <Link className="astra-button" to={`/ops/verify${scope.linkSearch({ actor: farmId })}`}>{t(m.verify)} <ArrowUpRight size={15} /></Link>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------ Карантин */

function QuarantineReview({ review }: ReviewByKindProps) {
  const t = useT();
  const rows = review.quarantine;
  return (
    <div className="review">
      <Section icon={ShieldAlert} title={t(m.secQuarantine)}>
        {rows === null ? <SkeletonList label={t(m.quarantineLabel)} items={3} />
          : rows.length === 0 ? <p className="review__empty">{t(m.quarantineEmpty)}</p>
          : (
            <ul className="review__list">
              {rows.slice(0, 8).map(r => (
                <li key={r.contourId} className="review__row">
                  <span className="review__name">{t(m.contourTitle, { no: r.contourNo })}</span>
                  <span className={`review__q review__q--${r.status}`}>{t(r.status === 'red' ? m.statusRed : r.status === 'yellow' ? m.statusYellow : m.statusGreen, { date: dateDay(r.since) })}</span>
                  <span className="review__meta">
                    {joinParts([
                      r.pest ?? t(m.pestNone),
                      r.deadline ? t(m.orderUntil, { date: dateDay(r.deadline) }) : t(m.orderNone),
                      r.overdueDays !== null && t(m.orderOverdue, { days: t(m.days, { count: r.overdueDays }) }),
                    ])}
                  </span>
                </li>
              ))}
            </ul>
          )}
      </Section>
    </div>
  );
}

/* ------------------------------------------------------------ Совпадение */

function OverlapReview(props: ReviewByKindProps) {
  const t = useT();
  const { review, situation, scope } = props;
  const irrigation = (review.irrigation ?? []).filter(r => !r.ahead);
  const silentIrrigation = irrigation.filter(r => r.silentContours.length > 0);
  const water = review.map.data.waterById;
  // Одни и те же контуры: без отчёта о поливе И с недоподачей по нему.
  const same = new Set<string>();
  if (water) for (const r of silentIrrigation) for (const id of r.silentContours) if (water[id] === 'none' || water[id] === 'partial') same.add(id);
  return (
    <div className="review">
      <Section icon={CalendarClock} title={t(m.secOverlap)}>
        {silentIrrigation.length ? (
          <ul className="review__list">
            {silentIrrigation.map(r => (
              <li key={r.workDefId} className="review__row">
                <WorkName scope={scope} w={r} />
                <span className="review__num">{countOf(r.silentContours.length, WORDS.contour)}</span>
                <span className="review__meta">{r.windowTo && r.windowTo < scope.asOf ? t(m.overlapMetaClosed, { date: dateDay(r.windowTo), days: t(m.days, { count: daysFrom(r.windowTo, scope.asOf) }) }) : t(m.overlapMetaOpen, { date: dateDay(r.windowTo) })}</span>
              </li>
            ))}
          </ul>
        ) : <p className="review__empty">{t(m.overlapEmpty)}</p>}
      </Section>
      <Section icon={Droplets} title={t(m.secSameIrrigation)}>
        <IrrigationList review={review} asOf={scope.asOf} />
      </Section>
      <p className="review__line">
        {same.size
          ? t(m.sameContours, { count: same.size, b: strong })
          : situation && situation.overlapObligations > 0
            ? t(m.sameNamed, { count: situation.overlapObligations, b: strong })
            : t(m.sameNone)}
      </p>
    </div>
  );
}
